import * as React from 'react';
import cx from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { State } from '../redux/state';
import { withLocalization, LProps } from '../utils/wrappers';
import { CompetitionEntityScore, Entity, CompetitionEntity, Competition } from '../userApi';
import { Link } from 'react-router-dom';
import { makeEntityPath } from '../utils/routerHelper';
import { departmentDivisionFromEntity, campusLabelFor } from '../utils/competitionLabels';
import { estimateEntityDollarSavings } from '../utils/actionSavings';

interface ManualTeamScoreProps {
  score: CompetitionEntityScore;
  rank: number;
  showDollarSavings: boolean;
  showWater: boolean;
  pointsOnly: boolean;
}

interface TeamScoreProps extends ManualTeamScoreProps {
  competitionEntity: CompetitionEntity;
  team: Entity;
  competition: Competition;
  dollarsSaved: number;
}

const TeamScore = ({
  rank, score, team, competitionEntity, competition, showDollarSavings, showWater,
  pointsOnly, dollarsSaved, t, formatNumber
}: LProps<TeamScoreProps>) => {
  if (!team || !competitionEntity) {
    return null;
  }
  const parsed = departmentDivisionFromEntity(team);
  const showDepartment = Boolean(parsed.department && parsed.department !== parsed.division);
  const waterGallons = Number(score.savingsWaterOz || 0);
  return (
  <tr>
    <td>#{rank}</td>
    <td>
      <Link to={makeEntityPath(competitionEntity.competitionID, team, t)}>
        {parsed.division || team.name}
      </Link>
      {showDepartment &&
        <div className="cc__lighttext">{`${campusLabelFor(competition, 'Department')}: ${parsed.department}`}</div>
      }
    </td>
    {!pointsOnly && (
      <td className="cc__scoreboard__percentage">{formatNumber(competitionEntity.memberCount || 0, 0)}</td>
    )}
    {!pointsOnly && (
      <td className="cc__scoreboard__percentage">{formatNumber(competitionEntity.pledgeCount || 0, 0)}</td>
    )}
    <td className="cc__scoreboard__points">{formatNumber(score.points, 0)}</td>
    {!pointsOnly && (
      <td className="cc__scoreboard__points">{formatNumber(score.savingsCO2Lbs || 0, 0)}</td>
    )}
    {showWater && (
      <td className="cc__scoreboard__points">{formatNumber(waterGallons, 0)}</td>
    )}
    {showDollarSavings && (
      <td className="cc__scoreboard__points">${formatNumber(Math.round(dollarsSaved), 0)}</td>
    )}
  </tr>
  );
};

const ConnectedTeamScore = compose(
  connect((state: State, ownProps: ManualTeamScoreProps) => ({
    competitionEntity: state.competitionEntities[ownProps.score.entityID],
    team: state.entities[ownProps.score.entityID],
    competition: state.competition,
    dollarsSaved: estimateEntityDollarSavings(ownProps.score, state.competitionPledges)
  })),
  withLocalization('scoreboard')
)(TeamScore) as React.ComponentClass<ManualTeamScoreProps>;

interface ManualProps {
  teamScores: CompetitionEntityScore[];
  limit?: number;
  /** Landing widget: denser type, omit water to keep the table readable. */
  compact?: boolean;
  /** Landing Divisions tab: reviewer requested rank/name/points only. */
  pointsOnly?: boolean;
}

interface Props extends ManualProps {
  showDollarSavings: boolean;
}

const ScoreboardTeams = ({
  t, teamScores, limit, showDollarSavings, compact, pointsOnly = false
}: LProps<Props>) => {
  const showWater = Boolean(showDollarSavings) && !compact && !pointsOnly;
  const showDollars = Boolean(showDollarSavings) && !pointsOnly;
  return (
  <div className="cc__scoreboard__table-wrap">
  <table className={cx({ 'cc__scoreboard__table--compact': compact })}>
    <thead>
      <tr>
        <th />
        <th />
        {!pointsOnly && (
          <th className="cc__scoreboard__percentage">{compact ? t('Part.') : t('Participants')}</th>
        )}
        {!pointsOnly && <th className="cc__scoreboard__percentage">{t('Actions')}</th>}
        <th className="cc__scoreboard__points">{t('Points')}</th>
        {!pointsOnly && (
          <th
            className="cc__scoreboard__points"
            title={t('Pounds CO2e saved')}
            dangerouslySetInnerHTML={{ __html: compact ? t('CO<sub>2</sub>e') : t('Lbs CO<sub>2</sub>e') }}
          />
        )}
        {showWater && (
          <th className="cc__scoreboard__points" title={t('Water saved (gallons)')}>{t('Water (gal)')}</th>
        )}
        {showDollars && (
          <th className="cc__scoreboard__points" title={t('Estimated San Francisco bill savings')}>{t('$ Saved')}</th>
        )}
      </tr>
    </thead>
    <tbody>
      {((limit ? teamScores && teamScores.slice(0, limit) : teamScores) || []).map((teamScore, i) => (
        <ConnectedTeamScore
          key={teamScore.entityID}
          score={teamScore}
          rank={i + 1}
          showDollarSavings={showDollars}
          showWater={showWater}
          pointsOnly={pointsOnly}
        />
      ))}
    </tbody>
  </table>
  </div>
  );
};

const mapStateToProps = (state: State, ownProps: ManualProps): Props => {
  const competitionName = String((state.competition && state.competition.name) || '').toLowerCase();
  return {
    teamScores: ownProps.teamScores,
    limit: ownProps.limit,
    compact: ownProps.compact,
    pointsOnly: ownProps.pointsOnly,
    showDollarSavings: competitionName === 'ccsf' || competitionName === 'sf' || competitionName === 'sfenvironment'
  };
};

export default compose(
  connect(mapStateToProps),
  withLocalization('scoreboard')
)(ScoreboardTeams) as React.SFC<ManualProps>;
